import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import { Button, Card, DatePicker, Select, Form, FormProps, Input } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { useQueryClient } from '@tanstack/react-query';

import QUERY_KEYS from 'services/api/queryKeys';
import { getTransactionTypeOptions, usePaymentStatusAccOptions } from 'constants/form';

import { generateExportFileName } from 'utils';
import 'styles/transactions.scss';
import { TransactionCodesResponse } from 'services/api/type/booking.type';
import { useTranslation } from 'react-i18next';
import { TransactionAcc } from 'components/modal/transactions-payment-report/trasaction-acc';
import { TransactionSummary } from 'components/modal/transactions-payment-report/transaction-acc-summary';
import ExportTransactionsAcc from 'components/common/export-transactions-acc';

const initStartDate = dayjs();
const initEndDate = dayjs().add(1, 'day');

function TransactionsReportAcc() {
  const { t } = useTranslation();
  const TRANSACTIONS_TYPE_OPTIONS = getTransactionTypeOptions(t);
  const PAYMENT_STATUS_ACC_OPTIONS = usePaymentStatusAccOptions(t);
  const [form] = Form.useForm();

  const queryClient = useQueryClient();
  const isIpadMobile = useMediaQuery({
    query: '(max-width: 991px)'
  });

  const [filterOptions, setFilterOptions] = useState<any>({
    start_date_acc: initStartDate.format('YYYY-MM-DD'),
    end_date_acc: initEndDate.format('YYYY-MM-DD')
  });

  // state of those components use for Export data
  const [selectedPaymentState, setSelectedPaymentState] = useState<string | undefined>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | undefined>('');

  // state of transactionV2 (transaction Codes)
  const [transactionCodes, setTransactionCodes] = useState<TransactionCodesResponse[]>([]);

  const mapTransactionPaymentTypes = (transactionCodes: TransactionCodesResponse[]) => {
    const options = transactionCodes.map(item => ({
      label: item.name,
      value: item.id
    }));

    return [{ label: t('common.statuses.all'), value: '' }, ...options];
  };

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GET_TRANSACTIONS_PAYMENT_V2, filterOptions]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const { rangeDate, paymentStatus, ...restFilterOptions } = truthyValues;

    if (rangeDate) {
      const startDate: Dayjs = rangeDate[0];
      const endDate: Dayjs = rangeDate[1];
      if (startDate) {
        _.set(restFilterOptions, 'start_date_acc', startDate.format('YYYY-MM-DD'));
      }
      if (endDate) {
        _.set(restFilterOptions, 'end_date_acc', endDate.format('YYYY-MM-DD'));
      }
    }

    if (paymentStatus) {
      _.set(restFilterOptions, 'payment_status', paymentStatus);
    }

    setFilterOptions(restFilterOptions);
  };

  return (
    <div className="pms-transactions">
      <TransactionSummary filterOptions={filterOptions} t={t} />
      <Card
        title={t('managePage.transactionPage.acc.title')}
        style={{ width: '100%' }}
        size="small"
      >
        <Form
          form={form}
          name="staying-guests-form"
          layout={isIpadMobile ? 'vertical' : 'inline'}
          initialValues={{
            rangeDate: [initStartDate, initEndDate],
            state: '',
            transaction_code_id: '',
            paymentStatus: ''
          }}
          onFinish={onFinish}
          autoComplete="off"
        >
          <div className="pms-transactions__filter-wrapper">
            <div className="pms-transactions__filter">
              <Form.Item name="rangeDate">
                <DatePicker.RangePicker
                  className="w-full"
                  placeholder={[t('common.time.startDate'), t('common.time.toDate')]}
                  format="DD-MM-YYYY"
                  // onChange={handleRangeDateChange}
                />
              </Form.Item>

              <Form.Item name="booking_id">
                <Input placeholder={t('common.bookingData.bookingId')} />
              </Form.Item>

              <Form.Item name="state">
                <Select
                  options={TRANSACTIONS_TYPE_OPTIONS}
                  labelRender={option => (
                    <span>
                      {t('common.bookingData.status')}: {option.label}
                    </span>
                  )}
                />
              </Form.Item>

              <Form.Item name="transaction_code_id">
                <Select
                  options={mapTransactionPaymentTypes(transactionCodes || [])}
                  labelRender={option => (
                    <span>
                      {t('managePage.transactionPage.common.paymentMethod')}: {option.label}
                    </span>
                  )}
                />
              </Form.Item>

              <Form.Item name="paymentStatus">
                <Select
                  options={PAYMENT_STATUS_ACC_OPTIONS}
                  labelRender={option => (
                    <span>
                      {t('managePage.transactionPage.acc.paymentStatus')}: {option.label}
                    </span>
                  )}
                />
              </Form.Item>

              <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                {t('common.actions.submitButton')}
              </Button>
            </div>
            <div className="pms-transactions__export">
              <ExportTransactionsAcc
                fileName={generateExportFileName('transactions-payment-acc')}
                selectedPaymentState={selectedPaymentState}
                selectedPaymentMethod={selectedPaymentMethod}
                filterOptions={filterOptions}
                t={t}
              />
            </div>
          </div>
        </Form>

        <div className="pms-transactions__table">
          <TransactionAcc
            filterOptions={filterOptions}
            setSelectedPaymentState={setSelectedPaymentState}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            setTransactionCodes={setTransactionCodes}
            t={t}
          />
        </div>
      </Card>
    </div>
  );
}

export default TransactionsReportAcc;
